import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Command as CommandIcon, ArrowRight, Building2, Users, Receipt,
  FileSearch, FileBarChart2, Calculator, CircleDollarSign, Sparkles,
  MessageSquare, Briefcase, Plug, CreditCard, Settings as SettingsIcon,
  LayoutDashboard, TrendingUp, Plus, Bell, UserCheck, History, StickyNote,
  Mail, X, Wand2, Flag, CalendarClock, Loader2, Check,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Global command palette (⌘K / Ctrl+K).
 * - Nav shortcuts to every accountant page
 * - Jump to any client (list fetched on first open, cached for the session)
 * - Per-client quick actions (impersonate, notes, remind, access history)
 * - Global actions (new task, generate AI debt plan, suggest tasks, preview digest)
 *
 * Deterministic fuzzy search — no LLM in v1. Fast, offline, private.
 */

// Static navigation entries
const NAV_ITEMS = [
  { id: "nav-overview", label: "Overview", hint: "Personal dashboard", icon: LayoutDashboard, to: "/app", kind: "nav" },
  { id: "nav-firm", label: "Firm Dashboard", hint: "Client book overview", icon: TrendingUp, to: "/app/firm", kind: "nav" },
  { id: "nav-transactions", label: "Transactions", icon: Receipt, to: "/app/transactions", kind: "nav" },
  { id: "nav-statements", label: "Statements", icon: FileSearch, to: "/app/statements", kind: "nav" },
  { id: "nav-reports", label: "Reports", icon: FileBarChart2, to: "/app/reports", kind: "nav" },
  { id: "nav-tax", label: "Tax Savings", icon: Calculator, to: "/app/tax", kind: "nav" },
  { id: "nav-debts", label: "Debt", icon: CircleDollarSign, to: "/app/debts", kind: "nav" },
  { id: "nav-advisor", label: "AI Advisor", icon: Sparkles, to: "/app/advisor", kind: "nav" },
  { id: "nav-clients", label: "Clients", icon: Users, to: "/app/clients", kind: "nav" },
  { id: "nav-backoffice", label: "Back Office", hint: "Task board", icon: Briefcase, to: "/app/back-office", kind: "nav" },
  { id: "nav-messages", label: "Messages", icon: MessageSquare, to: "/app/messages", kind: "nav" },
  { id: "nav-integrations", label: "Integrations", icon: Plug, to: "/app/integrations", kind: "nav" },
  { id: "nav-billing", label: "Billing", icon: CreditCard, to: "/app/billing", kind: "nav" },
  { id: "nav-settings", label: "Settings", icon: SettingsIcon, to: "/app/settings", kind: "nav" },
];

// Global actions (accountant, not client-scoped)
const buildGlobalActions = (navigate) => [
  {
    id: "act-new-task", label: "New task", hint: "Add to Back Office", icon: Plus, kind: "action",
    keywords: "todo add task create",
    run: () => navigate("/app/back-office?new=1"),
  },
  {
    id: "act-suggest-tasks", label: "Suggest tasks (AI)", hint: "Claude reviews your firm", icon: Sparkles, kind: "action",
    keywords: "ai claude suggest generate",
    run: () => navigate("/app/back-office?suggest=1"),
  },
  {
    id: "act-firm-summary", label: "Firm health summary (AI)", hint: "2-sentence exec summary", icon: Sparkles, kind: "action",
    keywords: "ai claude firm dashboard health summary",
    run: () => navigate("/app/firm"),
  },
  {
    id: "act-debt-plan", label: "Quickest way out of debt (AI)", icon: Sparkles, kind: "action",
    keywords: "ai debt payoff plan avalanche snowball",
    run: () => navigate("/app/debts?ai=1"),
  },
  {
    id: "act-preview-digest", label: "Preview weekly digest", hint: "Settings → Notifications", icon: Mail, kind: "action",
    keywords: "email digest preview resend weekly",
    run: () => navigate("/app/settings"),
  },
];

// Per-client actions template
const CLIENT_ACTIONS = [
  { label: "Jump to", icon: ArrowRight, key: "goto" },
  { label: "Impersonate", icon: UserCheck, key: "impersonate" },
  { label: "Send statement reminder", icon: Bell, key: "remind" },
  { label: "Internal notes", icon: StickyNote, key: "notes" },
  { label: "Access history", icon: History, key: "history" },
];

// Simple fuzzy scoring: substring boost + acronym boost + label-start boost.
function score(query, text, keywords = "") {
  const q = query.toLowerCase().trim();
  if (!q) return 1; // no query — everything shows in list order
  const t = text.toLowerCase();
  const k = keywords.toLowerCase();
  if (t === q) return 100;
  if (t.startsWith(q)) return 80;
  if (t.includes(q)) return 60;
  if (k.includes(q)) return 50;
  // Acronym match, e.g. "bo" -> "Back Office"
  const acronym = t.split(/[\s\-_]/).map(w => w[0]).filter(Boolean).join("");
  if (acronym.startsWith(q)) return 45;
  // Char-by-char subsequence — every char of q appears in t in order
  let i = 0;
  for (const c of t) {
    if (c === q[i]) i += 1;
    if (i === q.length) return 20;
  }
  return 0;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null); // client whose sub-actions are shown
  const [aiParsing, setAiParsing] = useState(false);
  const [aiParsed, setAiParsed] = useState(null); // parsed-command preview
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Global keyboard trigger
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus input + reset when opened; lazy-load clients once
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSelected(0);
    setExpandedClient(null);
    setAiParsed(null);
    setAiParsing(false);
    setTimeout(() => inputRef.current?.focus(), 30);
    if (!clientsLoaded) {
      api.get("/clients")
        .then(({ data }) => setClients(data.clients || []))
        .catch(() => setClients([]))
        .finally(() => setClientsLoaded(true));
    }
  }, [open, clientsLoaded]);

  const globalActions = useMemo(() => buildGlobalActions(navigate), [navigate]);

  // Build the filtered result list
  const results = useMemo(() => {
    if (expandedClient) {
      return CLIENT_ACTIONS.map((a) => ({
        id: `client-${expandedClient.id}-${a.key}`,
        label: a.label,
        hint: expandedClient.business_name,
        icon: a.icon,
        kind: "client-action",
        client: expandedClient,
        actionKey: a.key,
        _score: 100,
      }));
    }
    const q = query.trim();
    const scored = [];

    NAV_ITEMS.forEach((it) => {
      const s = score(q, it.label, it.hint);
      if (s > 0) scored.push({ ...it, _score: s + 3 }); // slight boost so nav wins on ties
    });

    globalActions.forEach((it) => {
      const s = score(q, it.label, `${it.hint || ""} ${it.keywords || ""}`);
      if (s > 0) scored.push({ ...it, _score: s + 2 });
    });

    clients.forEach((c) => {
      const s = score(q, c.business_name, `${c.email} ${c.name || ""}`);
      if (s > 0) {
        scored.push({
          id: `client-${c.id}`,
          label: c.business_name,
          hint: c.email,
          icon: Building2,
          kind: "client",
          client: c,
          _score: s + 1,
        });
      }
    });

    scored.sort((a, b) => b._score - a._score);
    const top = scored.slice(0, 40);
    // Append an AI parse suggestion when the user typed something command-like
    if (q.length >= 4 && !expandedClient) {
      top.push({
        id: "ai-parse",
        label: `Interpret as command: "${q}"`,
        hint: "Claude will turn this into a task you can review",
        icon: Wand2,
        kind: "ai-parse",
        _score: -1,
      });
    }
    return top;
  }, [query, clients, globalActions, expandedClient]);

  useEffect(() => { setSelected(0); }, [query, expandedClient]);

  // Keep the selected item in view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selected}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const runItem = async (item) => {
    if (item.kind === "nav") {
      setOpen(false);
      navigate(item.to);
      return;
    }
    if (item.kind === "action") {
      setOpen(false);
      item.run();
      return;
    }
    if (item.kind === "client") {
      // Expand into sub-actions for the client
      setExpandedClient(item.client);
      setQuery("");
      return;
    }
    if (item.kind === "client-action") {
      await runClientAction(item);
      return;
    }
    if (item.kind === "ai-parse") {
      setAiParsing(true);
      try {
        const { data } = await api.post("/back-office/parse-command", { text: query });
        if (data.intent !== "create_task" || !data.title) {
          toast.error("I couldn't turn that into a task — try being more specific.");
          setAiParsing(false);
          return;
        }
        setAiParsed(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI parse failed");
      } finally {
        setAiParsing(false);
      }
    }
  };

  const createParsedTask = async () => {
    if (!aiParsed) return;
    try {
      await api.post("/back-office/tasks", {
        title: aiParsed.title,
        description: aiParsed.description,
        client_id: aiParsed.client_id,
        priority: aiParsed.priority,
        due_date: aiParsed.due_date,
      });
      toast.success("Task created");
      setOpen(false);
      navigate("/app/back-office");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create task");
    }
  };

  const runClientAction = async (item) => {
    const { client, actionKey } = item;
    if (actionKey === "goto") {
      setOpen(false);
      navigate("/app/clients");
      return;
    }
    if (actionKey === "impersonate") {
      if (!window.confirm(`Sign in as ${client.business_name}?`)) return;
      try {
        await api.post(`/clients/${client.id}/impersonate`);
        window.location.href = "/client";
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not impersonate");
      }
      return;
    }
    if (actionKey === "remind") {
      setOpen(false);
      try {
        const { data } = await api.post(`/digest/clients/${client.id}/remind`);
        toast.success(`Reminder sent to ${data.sent_to}`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reminder");
      }
      return;
    }
    if (actionKey === "notes" || actionKey === "history") {
      // Both live on the Clients page; navigate and let user open the modal.
      setOpen(false);
      navigate(`/app/clients?client=${client.id}&open=${actionKey}`);
      return;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = results[selected];
      if (item) runItem(item);
    } else if (e.key === "Backspace" && !query && expandedClient) {
      e.preventDefault();
      setExpandedClient(null);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-24 bg-ink/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="command-palette-overlay"
    >
      <div
        className="w-full max-w-xl bg-white rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="command-palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-ink-lighter shrink-0" strokeWidth={1.75} />
          {expandedClient && (
            <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-forest/10 text-forest inline-flex items-center gap-1 shrink-0" data-testid="cp-context-pill">
              <Building2 className="w-2.5 h-2.5" strokeWidth={2} />
              {expandedClient.business_name}
              <button
                type="button"
                onClick={() => setExpandedClient(null)}
                className="ml-1 hover:text-ochre"
                data-testid="cp-context-clear"
              >
                <X className="w-2.5 h-2.5" strokeWidth={2.5} />
              </button>
            </span>
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={expandedClient ? "Pick an action…" : "Search pages, clients, actions… (⌘K)"}
            className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-ink-lighter"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline text-[10px] text-ink-lighter font-mono border border-border rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="overflow-y-auto flex-1" data-testid="command-palette-results">
          {aiParsed ? (
            <AiParsedPreview
              parsed={aiParsed}
              onConfirm={createParsedTask}
              onEdit={() => setAiParsed(null)}
            />
          ) : aiParsing ? (
            <div className="flex items-center gap-2 px-4 py-10 justify-center text-sm text-ink-lighter" data-testid="cp-ai-parsing">
              <Loader2 className="w-4 h-4 animate-spin text-forest" />
              Claude is parsing your command…
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-ink-lighter" data-testid="cp-empty">
              No matches for &ldquo;{query}&rdquo;.
            </div>
          ) : (
            <ul className="py-1">
              {results.map((r, idx) => {
                const Icon = r.icon;
                const isActive = idx === selected;
                return (
                  <li key={r.id} data-idx={idx}>
                    <button
                      type="button"
                      onMouseEnter={() => setSelected(idx)}
                      onClick={() => runItem(r)}
                      className={`w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm ${
                        isActive ? "bg-forest/[0.06] text-ink" : "text-ink hover:bg-paper/60"
                      }`}
                      data-testid={`cp-item-${r.id}`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
                        isActive ? "bg-forest text-white" : "bg-paper text-ink-light"
                      }`}>
                        <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate leading-snug">{r.label}</div>
                        {r.hint && <div className="text-xs text-ink-lighter truncate">{r.hint}</div>}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-lighter shrink-0">
                        {r.kind === "nav" ? "Go" :
                         r.kind === "action" ? "Run" :
                         r.kind === "client" ? "Client" :
                         r.kind === "ai-parse" ? "AI" :
                         "Do"}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer hints */}
        <div className="px-4 py-2.5 border-t border-border flex items-center gap-4 text-[10px] text-ink-lighter">
          <span className="inline-flex items-center gap-1"><kbd className="font-mono border border-border rounded px-1 py-0.5">↑↓</kbd> navigate</span>
          <span className="inline-flex items-center gap-1"><kbd className="font-mono border border-border rounded px-1 py-0.5">↵</kbd> select</span>
          {expandedClient && (
            <span className="inline-flex items-center gap-1"><kbd className="font-mono border border-border rounded px-1 py-0.5">⌫</kbd> back</span>
          )}
          <span className="ml-auto inline-flex items-center gap-1">
            <CommandIcon className="w-3 h-3" strokeWidth={1.75} />
            BizCashflow Pros
          </span>
        </div>
      </div>
    </div>
  );
}

function AiParsedPreview({ parsed, onConfirm, onEdit }) {
  const priCfg = {
    high: { bg: "bg-ochre/10", fg: "text-ochre" },
    medium: { bg: "bg-forest/10", fg: "text-forest" },
    low: { bg: "bg-paper", fg: "text-ink-lighter" },
  }[parsed.priority] || { bg: "bg-forest/10", fg: "text-forest" };
  const confCfg = {
    high: { fg: "text-forest", label: "High confidence" },
    medium: { fg: "text-ink-light", label: "Medium confidence" },
    low: { fg: "text-ochre", label: "Low confidence" },
  }[parsed.confidence] || { fg: "text-ink-light", label: "Medium confidence" };

  return (
    <div className="p-5" data-testid="cp-ai-preview">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-forest/10 text-forest flex items-center justify-center shrink-0">
          <Wand2 className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="overline">Parsed by Claude</div>
          <div className="text-xs text-ink-lighter italic mt-0.5">&ldquo;{parsed.original}&rdquo;</div>
        </div>
        <div className={`text-[10px] ${confCfg.fg} shrink-0`}>{confCfg.label}</div>
      </div>

      <div className="bg-paper/50 border border-border rounded-lg p-4 space-y-2.5" data-testid="cp-ai-preview-card">
        <div className="text-base font-medium leading-snug" data-testid="cp-ai-preview-title">{parsed.title}</div>
        {parsed.description && (
          <div className="text-sm text-ink-light">{parsed.description}</div>
        )}
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 ${priCfg.bg} ${priCfg.fg} inline-flex items-center gap-1`}>
            <Flag className="w-2.5 h-2.5" strokeWidth={2} />
            {parsed.priority.charAt(0).toUpperCase() + parsed.priority.slice(1)}
          </span>
          {parsed.client_business_name && (
            <span className="text-[10px] text-ink-light inline-flex items-center gap-1">
              <Building2 className="w-2.5 h-2.5" strokeWidth={1.75} />
              {parsed.client_business_name}
            </span>
          )}
          {!parsed.client_id && (
            <span className="text-[10px] text-ink-lighter italic">Firm-general</span>
          )}
          {parsed.due_date && (
            <span className="text-[10px] text-ink-light inline-flex items-center gap-1 num">
              <CalendarClock className="w-2.5 h-2.5" strokeWidth={2} />
              {parsed.due_date}
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onEdit}
          className="btn-secondary text-sm"
          data-testid="cp-ai-preview-cancel"
        >
          Try different words
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="btn-forest text-sm inline-flex items-center gap-1.5"
          data-testid="cp-ai-preview-create"
        >
          <Check className="w-3.5 h-3.5" />
          Create task
        </button>
      </div>
    </div>
  );
}

